import { useCallback, useEffect, useRef, useState } from "react";
import { takePendingFile } from "@/lib/pending-upload";
import { motion, AnimatePresence } from "framer-motion";
import { Copy, Check, Download, Upload, ChevronDown, Braces, Table, Palette, Image as ImageIcon } from "lucide-react";
import { Shell } from "@/components/layout/Shell";
import { useSeo } from "@/hooks/use-seo";
import { AdSlot } from "@/components/ads/AdSlot";
import { ShareButtons } from "@/components/social/ShareButtons";
import {
  buildColorReport,
  ColorReport,
  ABSOLUTE_HEX,
  buildJsonExport,
  buildCsvExport,
  buildCssExport,
} from "@/lib/color-grabber";
import { cn } from "@/lib/utils";

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** Renders a shareable palette card (swatches + names + hex + coverage) to a
 * PNG — meant for dropping straight into a moodboard, deck, or design file
 * without needing to open any other tool. */
function exportPalettePng(report: ColorReport) {
  const rowHeight = 64;
  const width = 640;
  const padding = 24;
  const headerHeight = 70;
  const height = headerHeight + report.groups.length * rowHeight + padding;

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  ctx.fillStyle = "#FBF9F4";
  ctx.fillRect(0, 0, width, height);

  ctx.fillStyle = "#1A1A1A";
  ctx.font = "600 24px Georgia, serif";
  ctx.fillText("Color Grabber Palette", padding, 40);
  ctx.font = "13px Arial, sans-serif";
  ctx.fillStyle = "#6B6B6B";
  ctx.fillText("mychromamind.com/color-grabber", padding, 60);

  report.groups.forEach((g, i) => {
    const y = headerHeight + i * rowHeight;
    const swatchSize = 44;
    const swatchY = y + (rowHeight - swatchSize) / 2;

    ctx.fillStyle = g.hex;
    ctx.beginPath();
    ctx.roundRect(padding, swatchY, swatchSize, swatchSize, 10);
    ctx.fill();
    ctx.strokeStyle = "#00000022";
    ctx.lineWidth = 1;
    ctx.stroke();

    ctx.fillStyle = "#1A1A1A";
    ctx.font = "600 16px Arial, sans-serif";
    ctx.fillText(g.name, padding + swatchSize + 16, y + rowHeight / 2 - 4);

    ctx.fillStyle = "#6B6B6B";
    ctx.font = "13px monospace";
    ctx.fillText(g.hex, padding + swatchSize + 16, y + rowHeight / 2 + 16);

    const pct = `${Math.round(g.weight * 100)}%`;
    ctx.font = "600 16px Arial, sans-serif";
    ctx.textAlign = "right";
    ctx.fillStyle = "#1A1A1A";
    ctx.fillText(pct, width - padding, y + rowHeight / 2 + 5);
    ctx.textAlign = "left";
  });

  canvas.toBlob((blob) => {
    if (blob) downloadBlob(blob, "color-grabber-palette.png");
  }, "image/png");
}

type Status = "idle" | "scanning" | "success" | "error";

const MAX_FILE_BYTES = 8 * 1024 * 1024; // 8MB

const HOW_IT_WORKS =
  "Color Grabber reads your photo directly in the browser and samples every pixel into a fine-grained histogram, exactly the way Background Maker and Aura Scanner do. From there it takes a different approach to naming what it sees: instead of reporting the raw, arbitrary hex value under each pixel, it converts every sampled shade into hue, saturation, and lightness and sorts it into the color family a person would actually use to describe it — red, orange, yellow, brown, green, teal, blue, purple, pink, white, gray, or black. A sunlit highlight and a shadowed fold of the same fabric usually land on very different hex codes, but if they're both perceptually \"red\", they're counted as the same color and their coverage is added together, so the final list matches how a human eye actually reads the photo rather than how a color picker would. Alongside that grouped list, the True Colors row shows the pure, textbook version of each detected family — the red everyone pictures when they hear \"red\" — while the Colors Used list keeps the exact detected shades and their individual hex codes for anyone who needs that level of detail. The Palette Analysis panel then looks at the whole set of colors together to work out the dominant hue, whether the image leans warm or cool, how bright and saturated it is on average, what kind of palette it forms (vibrant, muted, pastel, dark, or monochrome), how the colors relate to each other on the color wheel (monochromatic, complementary, triadic, analogous, or multicolor), a suggested complementary color, and which text color would read best against the dominant shade. Nothing in this process ever leaves your device — the photo is decoded straight into a canvas element and analyzed locally, so there's no upload, no server round-trip, and no copy of your image stored anywhere.";

const TEMPERATURE_LABEL: Record<ColorReport["temperature"], string> = {
  warm: "Warm",
  cool: "Cool",
  neutral: "Neutral",
};

const PALETTE_LABEL: Record<ColorReport["paletteType"], string> = {
  vibrant: "Vibrant",
  pastel: "Pastel",
  muted: "Muted",
  dark: "Dark & Moody",
  monochrome: "Monochrome",
  balanced: "Balanced",
};

function CopyHex({ hex }: { hex: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(hex);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    } catch {
      // clipboard unavailable — silently ignore, hex is still visible to copy by hand
    }
  };
  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label={`Copy ${hex}`}
      className="inline-flex items-center gap-1 font-mono text-xs sm:text-sm font-medium text-foreground hover:text-primary transition-colors"
    >
      {hex}
      {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5 opacity-60" />}
    </button>
  );
}

export default function ColorGrabber() {
  useSeo({
    title: "Color Grabber — Automatically Extract Named Colors from Any Photo | Mychromamind",
    description:
      "Upload a photo and Color Grabber automatically detects every color used in it — grouped by human-perceived color name (red, blue, green, brown…) rather than raw pixel shades — with hex codes, coverage percentages, and a full palette analysis.",
    path: "/color-grabber",
    ogType: "website",
    structuredData: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebApplication",
          name: "Color Grabber",
          applicationCategory: "DesignApplication",
          operatingSystem: "Any (Web)",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
          description:
            "Upload a photo to automatically detect every color used in it, grouped by human-perceived color name, with hex codes, coverage percentages, and full palette analysis.",
          url: "https://mychromamind.com/color-grabber",
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Mychromamind", item: "https://mychromamind.com/" },
            { "@type": "ListItem", position: 2, name: "Color Grabber", item: "https://mychromamind.com/color-grabber" },
          ],
        },
      ],
    },
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [status, setStatus] = useState<Status>("idle");
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [report, setReport] = useState<ColorReport | null>(null);
  const [error, setError] = useState("");

  const runScan = useCallback((url: string) => {
    setStatus("scanning");
    setError("");
    const img = new Image();
    img.onload = () => {
      // Small artificial delay so the analysis reads as a real pass rather
      // than an instant flash — the scan itself is effectively instant.
      window.setTimeout(() => {
        try {
          const result = buildColorReport(img);
          if (!result) {
            setError("Couldn't read any color data from that image. Try a different photo.");
            setStatus("error");
            return;
          }
          setReport(result);
          setStatus("success");
        } catch {
          setError("Something went wrong while analyzing that image. Try a different photo.");
          setStatus("error");
        }
      }, 800);
    };
    img.onerror = () => {
      setError("That file couldn't be loaded as an image. Try a JPG or PNG.");
      setStatus("error");
    };
    img.src = url;
  }, []);

  const handleFile = useCallback((file: File | undefined) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Please choose an image file (JPG, PNG, WEBP).");
      setStatus("error");
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setError("That image is too large — please choose one under 8MB.");
      setStatus("error");
      return;
    }
    const url = URL.createObjectURL(file);
    setImageUrl(url);
    runScan(url);
  }, [runScan]);

  // Auto-load a file passed from the homepage via the pending-upload store.
  useEffect(() => {
    const pending = takePendingFile();
    if (pending) handleFile(pending);
  }, [handleFile]);

  const handleReset = () => {
    setStatus("idle");
    setImageUrl(null);
    setReport(null);
    setError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const [exportOpen, setExportOpen] = useState(false);
  const exportMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!exportOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target as Node)) {
        setExportOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [exportOpen]);

  const EXPORT_OPTIONS = [
    {
      id: "json",
      label: "JSON",
      hint: "Full data — for developers & scripts",
      icon: Braces,
      run: (r: ColorReport) => {
        const blob = new Blob([JSON.stringify(buildJsonExport(r), null, 2)], { type: "application/json" });
        downloadBlob(blob, "color-grabber-report.json");
      },
    },
    {
      id: "csv",
      label: "CSV",
      hint: "Color list — for spreadsheets",
      icon: Table,
      run: (r: ColorReport) => {
        const blob = new Blob([buildCsvExport(r)], { type: "text/csv" });
        downloadBlob(blob, "color-grabber-colors.csv");
      },
    },
    {
      id: "css",
      label: "CSS Variables",
      hint: "Ready-to-use --color tokens",
      icon: Palette,
      run: (r: ColorReport) => {
        const blob = new Blob([buildCssExport(r)], { type: "text/css" });
        downloadBlob(blob, "color-grabber-colors.css");
      },
    },
    {
      id: "png",
      label: "Palette Image",
      hint: "Shareable PNG swatch card",
      icon: ImageIcon,
      run: (r: ColorReport) => exportPalettePng(r),
    },
  ] as const;

  const handleExport = (run: (r: ColorReport) => void) => {
    if (!report) return;
    run(report);
    setExportOpen(false);
  };

  return (
    <Shell>
      <div className="max-w-4xl mx-auto px-5 sm:px-6 py-10 md:py-16 w-full">
        <h1 className="text-3xl sm:text-5xl font-serif mb-4 leading-tight text-center">
          Color Grabber
        </h1>
        <p className="text-muted-foreground text-base sm:text-lg text-center max-w-2xl mx-auto mb-10">
          Upload any photo and it's analyzed automatically — every color is grouped by the name a
          person would actually call it (red, blue, brown…), not by exact pixel shade, so a bright
          highlight and a shadowed patch of the same color count as one. You get hex codes,
          coverage percentages, and a full breakdown of the palette's mood. All in your browser —
          your photo is never uploaded anywhere.
        </p>

        {status !== "success" && (
          <div className="max-w-lg mx-auto">
            <label
              htmlFor="color-grabber-file-input"
              className="group flex flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-border hover:border-primary/50 bg-card/60 hover:bg-card transition-colors cursor-pointer px-6 py-14 text-center"
            >
              <span className="text-4xl" aria-hidden="true">🎯</span>
              <span className="font-medium text-foreground">
                {status === "scanning" ? "Grabbing colors…" : "Click to upload a photo"}
              </span>
              <span className="text-xs text-muted-foreground">JPG, PNG, or WEBP — up to 8MB</span>
              <input
                id="color-grabber-file-input"
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="sr-only"
                disabled={status === "scanning"}
                onChange={(e) => handleFile(e.target.files?.[0])}
              />
            </label>

            <AnimatePresence>
              {status === "scanning" && imageUrl && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="relative mt-6 rounded-2xl overflow-hidden border border-border"
                >
                  <img src={imageUrl} alt="Uploaded photo being analyzed" className="w-full h-64 object-cover" />
                  <motion.div
                    className="absolute inset-x-0 h-1/3 bg-gradient-to-b from-white/40 to-transparent"
                    initial={{ top: "-33%" }}
                    animate={{ top: ["-33%", "100%"] }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  />
                  <div className="absolute bottom-3 left-0 right-0 text-center text-xs font-medium text-white drop-shadow">
                    Grouping colors by name…
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {status === "error" && (
              <div role="alert" className="mt-4 p-4 rounded-xl border border-destructive/30 bg-destructive/5 text-sm text-destructive">
                {error}
              </div>
            )}
          </div>
        )}

        {status === "success" && report && imageUrl && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            {/* Photo + dominant strip */}
            <div className="flex flex-col sm:flex-row items-center gap-6 mb-8">
              <img
                src={imageUrl}
                alt="Your uploaded photo"
                className="w-full sm:w-56 h-56 rounded-2xl object-cover border border-border shadow-md shrink-0"
              />
              <div className="flex-1 w-full">
                <p className="text-xs uppercase tracking-widest text-muted-foreground font-medium mb-2">
                  {report.groups.length} colors detected
                </p>
                <div className="flex rounded-xl overflow-hidden h-10 sm:h-12 border border-border mb-2">
                  {report.groups.map((g) => (
                    <div
                      key={g.name}
                      style={{ backgroundColor: g.hex, width: `${Math.max(g.weight * 100, 2)}%` }}
                      title={`${g.name} · ${Math.round(g.weight * 100)}%`}
                    />
                  ))}
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">{report.moodSummary}</p>
              </div>
            </div>

            {/* True colors — the pure, textbook version of each detected color name */}
            <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">
              True Colors
            </h2>
            <p className="text-xs text-muted-foreground -mt-2 mb-3">
              The pure version of each color detected in your photo — regardless of the exact
              shade, tint, or shadow it appeared in.
            </p>
            <div className="flex flex-wrap gap-3 mb-8">
              {report.groups.map((g) => (
                <div
                  key={`absolute-${g.name}`}
                  className="flex items-center gap-2 rounded-full border border-border bg-card pl-1.5 pr-4 py-1.5"
                >
                  <span
                    className="w-8 h-8 rounded-full shrink-0 border border-border/60"
                    style={{ backgroundColor: ABSOLUTE_HEX[g.name] }}
                  />
                  <span className="text-sm font-medium">{g.name}</span>
                  <span className="text-xs text-muted-foreground">{Math.round(g.weight * 100)}%</span>
                </div>
              ))}
            </div>

            {/* Named color list */}
            <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">
              Colors Used
            </h2>
            <div className="grid sm:grid-cols-2 gap-3 mb-8">
              {report.groups.map((g) => (
                <div key={g.name} className="rounded-xl border border-border bg-card p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="w-10 h-10 rounded-full shrink-0 border border-border/60" style={{ backgroundColor: g.hex }} />
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm">{g.name}</p>
                      <CopyHex hex={g.hex} />
                    </div>
                    <span className="text-sm font-semibold text-muted-foreground shrink-0">
                      {Math.round(g.weight * 100)}%
                    </span>
                  </div>
                  {g.shades.length > 1 && (
                    <div className="flex items-center gap-1.5 pt-2 border-t border-border/60">
                      {g.shades.map((s) => (
                        <span
                          key={s.hex}
                          className="w-5 h-5 rounded-full border border-border/60"
                          style={{ backgroundColor: s.hex }}
                          title={`${s.hex} · ${Math.round(s.weight * 100)}% of image`}
                        />
                      ))}
                      <span className="text-[11px] text-muted-foreground ml-1">
                        {g.shades.length} shade{g.shades.length > 1 ? "s" : ""} of {g.name.toLowerCase()}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Palette analysis */}
            <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">
              Palette Analysis
            </h2>
            <div className="grid sm:grid-cols-3 gap-3 mb-8">
              <div className="rounded-xl border border-border bg-card p-4">
                <p className="text-xs text-muted-foreground mb-1">Dominant Color</p>
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full shrink-0" style={{ backgroundColor: report.dominant.hex }} />
                  <p className="font-medium text-sm">{report.dominant.name}</p>
                </div>
              </div>
              <div className="rounded-xl border border-border bg-card p-4">
                <p className="text-xs text-muted-foreground mb-1">Temperature</p>
                <p className="font-medium text-sm">{TEMPERATURE_LABEL[report.temperature]}</p>
              </div>
              <div className="rounded-xl border border-border bg-card p-4">
                <p className="text-xs text-muted-foreground mb-1">Palette Type</p>
                <p className="font-medium text-sm">{PALETTE_LABEL[report.paletteType]}</p>
              </div>
              <div className="rounded-xl border border-border bg-card p-4">
                <p className="text-xs text-muted-foreground mb-1">Harmony</p>
                <p className="font-medium text-sm">{report.harmony}</p>
              </div>
              <div className="rounded-xl border border-border bg-card p-4">
                <p className="text-xs text-muted-foreground mb-1">Brightness</p>
                <div className="h-1.5 rounded-full bg-muted overflow-hidden mt-2">
                  <div className="h-full bg-foreground/70" style={{ width: `${report.avgBrightness}%` }} />
                </div>
                <p className="text-xs text-muted-foreground mt-1">{report.avgBrightness}/100</p>
              </div>
              <div className="rounded-xl border border-border bg-card p-4">
                <p className="text-xs text-muted-foreground mb-1">Saturation</p>
                <div className="h-1.5 rounded-full bg-muted overflow-hidden mt-2">
                  <div className="h-full" style={{ width: `${report.avgSaturation}%`, backgroundColor: report.dominant.hex }} />
                </div>
                <p className="text-xs text-muted-foreground mt-1">{report.avgSaturation}/100</p>
              </div>
            </div>

            {/* Complementary + accessibility */}
            <div className="grid sm:grid-cols-2 gap-3 mb-8">
              <div className="rounded-xl border border-border bg-card p-4 flex items-center gap-3">
                <span className="w-9 h-9 rounded-full shrink-0 border border-border/60" style={{ backgroundColor: report.complementaryHex }} />
                <div>
                  <p className="text-xs text-muted-foreground">Suggested Complementary Color</p>
                  <CopyHex hex={report.complementaryHex} />
                </div>
              </div>
              <div
                className="rounded-xl border border-border p-4 flex items-center justify-between"
                style={{ backgroundColor: report.dominant.hex, color: report.contrastText }}
              >
                <p className="text-xs font-medium">
                  Best text color on this dominant color: {report.contrastText === "#FFFFFF" ? "White" : "Dark / near-black"}
                </p>
              </div>
            </div>

            {/* How it works — real, substantive copy for search engines and
                to keep this results view above AdSense's thin-content bar */}
            <div className="max-w-2xl mx-auto mb-8">
              <h2 className="text-xl font-serif mb-3 text-center">How Color Grabber Works</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">{HOW_IT_WORKS}</p>
            </div>

            <AdSlot slotId="2431849490" content={[HOW_IT_WORKS, report.moodSummary]} className="my-8 max-w-2xl mx-auto" />

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10">
              <div className="relative" ref={exportMenuRef}>
                <button
                  type="button"
                  onClick={() => setExportOpen((v) => !v)}
                  aria-expanded={exportOpen}
                  aria-haspopup="menu"
                  className={cn(
                    "inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors font-medium text-sm",
                  )}
                >
                  <Download className="w-4 h-4" />
                  Export
                  <ChevronDown className={cn("w-4 h-4 transition-transform", exportOpen && "rotate-180")} />
                </button>

                <AnimatePresence>
                  {exportOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 6 }}
                      transition={{ duration: 0.15 }}
                      role="menu"
                      className="absolute z-10 bottom-full mb-2 left-1/2 -translate-x-1/2 w-72 rounded-2xl border border-border bg-popover shadow-xl overflow-hidden"
                    >
                      {EXPORT_OPTIONS.map((opt) => (
                        <button
                          key={opt.id}
                          type="button"
                          role="menuitem"
                          onClick={() => handleExport(opt.run)}
                          className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-muted transition-colors"
                        >
                          <opt.icon className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" />
                          <span>
                            <span className="block text-sm font-medium">{opt.label}</span>
                            <span className="block text-xs text-muted-foreground">{opt.hint}</span>
                          </span>
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <button
                type="button"
                onClick={handleReset}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-border hover:bg-muted transition-colors font-medium text-sm"
              >
                <Upload className="w-4 h-4" />
                Analyze Another Photo
              </button>
            </div>

            <ShareButtons
              label="Share Color Grabber"
              text="I just analyzed a photo's color palette with Mychromamind's Color Grabber — try it on your own photo:"
              className="mb-4"
            />

            <p className="text-center text-xs text-muted-foreground max-w-md mx-auto">
              Color names are based on human color perception, not exact pixel values — shades of
              the same hue are grouped together the way a person would describe them. Your photo is
              processed entirely in your browser and never uploaded to a server.
            </p>
          </motion.div>
        )}
      </div>
    </Shell>
  );
}
